// useUpdateGoals.js
//
// Owns 100% of the SAVE side of the Edit Goals modal: the POST call itself,
// and every piece of state related to it (`saving`, `saveError`,
// `validationErrors`, `lastSavedAt`). useEditGoals.js holds none of this —
// it only knows how to absorb a successful save's result via its
// commitDraft() function. This hook is the one that calls dataPoster.js's
// fetchUpdateGoals() and decides what "saving" / "failed" / "saved" means.
//
// ── Why a separate hook instead of folding this into useEditGoals ─────────────
//
// Splitting the read/draft concern (useEditGoals) from the write/POST
// concern (this hook) mirrors how dataReceiver.js / dataPoster.js are
// already split by HTTP verb on the backend side, and keeps useEditGoals
// usable with zero network-write code in it if something else ever needs
// read-only access to the same draft.
//
// ── lineLeader passthrough ────────────────────────────────────────────────────
//
// This hook is transparent to lineLeader — it passes the entire payload object
// to fetchUpdateGoals() without inspecting it.  The lineLeader field lives on
// payload.weeklyRollGoals and is read by dataPoster.js → dataPost.js in the
// next steps of the chain.  No changes to this hook's runtime behaviour are
// required to support the new field.
//
// ── Contract ──────────────────────────────────────────────────────────────────
//
// updateGoals() NEVER throws — it always resolves to a result object:
//   { ok: true,  message, updated }                     success
//   { ok: false, message, validationErrors }             rejected (400) or failed (500/network)
//   { ok: false, message: "Cancelled.", aborted: true }  superseded by a newer save call
//
// On success, the caller (editGoals.js) is responsible for calling
// useEditGoals's commitDraft(result.updated) — this hook has no reference to
// useEditGoals and never touches its state directly.
//
// ── Usage (see editGoals.js for the full integration) ────────────────────────
//
//   const { saving, saveError, validationErrors, updateGoals, clearError } =
//     useUpdateGoals();
//
//   const handleSave = async () => {
//     const result = await updateGoals(payload);
//     if (result.ok) commitDraft(result.updated); // from useEditGoals
//   };

import { useState, useRef, useCallback, useEffect } from "react";
import { fetchUpdateGoals, DataPosterError } from "../../utils/dataPoster";

/**
 * @typedef {{
 *   weeklyRollGoals:    Array<{ date: string, goalType: "roll" | "wrap", goalId: string | null }>,
 *   productionGoals:    Array<{ goalName: string, goalId: string | null }>,
 *   ytdCompletionGoals: Array<{ monthDate: string, goalId: string | null }>,
 * }} UpdatedGoalIds
 *
 * @typedef {{
 *   ok:                boolean,
 *   message:           string,
 *   updated?:          UpdatedGoalIds,
 *   validationErrors?: Array<{ where: string, field: string, message: string }>,
 *   aborted?:          boolean,
 * }} UpdateGoalsResult
 */

/**
 * Drives the POST side of the Edit Goals modal.
 *
 * @returns {{
 *   saving:            boolean,
 *   saveError:         string | null,
 *   validationErrors:  Array<{ where: string, field: string, message: string }>,
 *   lastSavedAt:       Date | null,
 *   updateGoals:       (payload: object) => Promise<UpdateGoalsResult>,
 *   clearError:        () => void,
 * }}
 */
const useUpdateGoals = () => {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Tracks the in-flight request's controller so a second Save click can
  // abort the first one rather than letting two writes race against the
  // backend (whichever response lands last would otherwise "win" silently).
  const controllerRef = useRef(null);

  // Abort any in-flight save on unmount — a component that's gone shouldn't
  // keep updating state when the response eventually arrives.
  useEffect(() => {
    return () => controllerRef.current?.abort();
  }, []);

  /** Clear any previously-shown save error/validation state. */
  const clearError = useCallback(() => {
    setSaveError(null);
    setValidationErrors([]);
  }, []);

  /**
   * POST `payload` to dataPost.js's update-goals route.
   *
   * The payload is passed through to fetchUpdateGoals() without inspection.
   * This hook is intentionally transparent to new fields — adding lineLeader
   * to weeklyRollGoals required no runtime changes here; it is read downstream
   * by dataPoster.js and written by dataPost.js's handleUpdateGoals.
   *
   * @param {{
   *   weeklyRollGoals?: {
   *     lineLeader?: string | null,   ← week-level roll leader name (new)
   *     days: Array<{ date: string, roll: number, wrap: number }>,
   *   },
   *   productionGoals?:    Array<{ goalName: string, goalValue: number }>,
   *   ytdCompletionGoals?: { months: Array<{ monthDate: string, goalValue: number }> },
   * }} payload
   *   Same shape as useEditGoals's draft objects; extra fields on each row
   *   (dayLabel, goalId, updatedAt, hasGoal, rollGoalId, wrapGoalId, etc.)
   *   are accepted and ignored by the backend — the draft objects can be sent
   *   exactly as-is without reshaping.
   *
   * @returns {Promise<UpdateGoalsResult>}
   */
  const updateGoals = useCallback(async (payload) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setSaving(true);
    setSaveError(null);
    setValidationErrors([]);

    try {
      const result = await fetchUpdateGoals(payload, controller.signal);
      setLastSavedAt(new Date());
      return { ok: true, message: "Saved.", updated: result.updated };
    } catch (err) {
      if (err.name === "AbortError") {
        // Superseded by a newer Save click — this call lost the race.
        // Deliberately leave saveError/validationErrors untouched; whatever
        // the NEWER call's outcome is should be the one the user sees.
        return { ok: false, message: "Cancelled.", aborted: true };
      }

      const message =
        err instanceof DataPosterError
          ? err.message
          : (err.message ?? "Failed to save goals.");
      const errs = err instanceof DataPosterError ? err.validationErrors : [];

      setSaveError(message);
      setValidationErrors(errs);

      return { ok: false, message, validationErrors: errs };
    } finally {
      // Only clear `saving` if THIS call's controller is still the active
      // one — an aborted (superseded) call's `finally` shouldn't stomp on
      // the newer call's `saving: true` state.
      if (controllerRef.current === controller) {
        setSaving(false);
      }
    }
  }, []);

  return {
    saving,
    saveError,
    validationErrors,
    lastSavedAt,
    updateGoals,
    clearError,
  };
};

export default useUpdateGoals;
